import React, { useEffect, useMemo, useRef, useState } from 'react';

import InputIconByKey from '../Input/InputIconByKey';
import RequiredAsteriskTooltip from '../Input/RequiredAsteriskTooltip';
import { IconKey } from '../../../types/system/icons';

interface TimePickerProps {
  label: string;
  name: string;
  value: string;
  onChange?: (time: string) => void;
  error?: string;
  disabled?: boolean;
  iconKey?: IconKey;
  required?: boolean;
  helperText?: string;
  placeholder?: string;
}

const hours = Array.from({ length: 12 }, (_, index) =>
  String(index + 1).padStart(2, '0')
);

const minutes = Array.from({ length: 60 }, (_, index) =>
  String(index).padStart(2, '0')
);

const periods = ['AM', 'PM'];

const parseTime = (value: string) => {
  if (!value) return { hour: '12', minute: '00', period: 'AM' };

  const [rawHour = '0', rawMinute = '00'] = value.split(':');
  const hour24 = Number(rawHour);
  const period = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 || 12;

  return {
    hour: String(hour12).padStart(2, '0'),
    minute: rawMinute.padStart(2, '0'),
    period,
  };
};

const to24Hour = (hour: string, minute: string, period: string) => {
  let hourNumber = Number(hour);

  if (period === 'AM' && hourNumber === 12) hourNumber = 0;
  if (period === 'PM' && hourNumber !== 12) hourNumber += 12;

  return `${String(hourNumber).padStart(2, '0')}:${minute}`;
};

const TimePicker: React.FC<TimePickerProps> = ({
  label,
  name,
  value,
  onChange,
  error,
  disabled = false,
  iconKey = 'clock',
  required,
  helperText,
  placeholder = 'Seleccione una hora',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(() => parseTime(value), [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const updateTime = (
    hour = selected.hour,
    minute = selected.minute,
    period = selected.period
  ) => {
    onChange?.(to24Hour(hour, minute, period));
  };

  const displayValue = value
    ? `${selected.hour}:${selected.minute} ${selected.period}`
    : placeholder;

  return (
    <div className="relative mb-5 w-full" ref={containerRef}>
      <label
        htmlFor={name}
        className="mb-2.5 flex items-center gap-1 text-sm font-medium text-black dark:text-white"
      >
        {label}
        {required && <RequiredAsteriskTooltip delay={400} />}
      </label>

      <div
        className={`
          bg-gray-1 flex w-full overflow-hidden rounded-xl border
          transition-all duration-200
          focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/10
          dark:bg-meta-4
          ${error ? 'border-danger' : 'border-stroke dark:border-strokedark'}
          ${disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}
        `}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
      >
        {iconKey && (
          <div className="flex shrink-0 items-center justify-center border-r border-stroke bg-gray-2 px-4 dark:border-strokedark dark:bg-boxdark-2">
            <InputIconByKey iconKey={iconKey} />
          </div>
        )}

        <input
          id={name}
          type="text"
          name={name}
          value={displayValue}
          readOnly
          disabled={disabled}
          className="w-full cursor-pointer bg-transparent px-4 py-3.5 text-sm text-black outline-none placeholder:text-body dark:text-white dark:placeholder:text-bodydark2"
        />
      </div>

      {isOpen && (
        <div className="absolute left-0 top-full z-999999 mt-0.5 w-full min-w-[260px] overflow-hidden rounded-xl border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
          <div className="grid grid-cols-3 border-b border-stroke text-center text-xs font-semibold uppercase text-body dark:border-strokedark dark:text-bodydark2">
            <div className="py-2">Hora</div>
            <div className="py-2">Min</div>
            <div className="py-2">Periodo</div>
          </div>

          <div className="grid max-h-64 grid-cols-3">
            <div className="max-h-64 overflow-y-auto">
              {hours.map((hour) => (
                <button
                  key={hour}
                  type="button"
                  onClick={() => updateTime(hour)}
                  className={`block w-full px-4 py-2.5 text-sm font-medium transition ${
                    selected.hour === hour
                      ? 'bg-primary text-white'
                      : 'text-black hover:bg-slate-100 dark:text-white dark:hover:bg-meta-4'
                  }`}
                >
                  {hour}
                </button>
              ))}
            </div>

            <div className="max-h-64 overflow-y-auto border-x border-stroke dark:border-strokedark">
              {minutes.map((minute) => (
                <button
                  key={minute}
                  type="button"
                  onClick={() => updateTime(undefined, minute)}
                  className={`block w-full px-4 py-2.5 text-sm font-medium transition ${
                    selected.minute === minute
                      ? 'bg-primary text-white'
                      : 'text-black hover:bg-slate-100 dark:text-white dark:hover:bg-meta-4'
                  }`}
                >
                  {minute}
                </button>
              ))}
            </div>

            <div>
              {periods.map((period) => (
                <button
                  key={period}
                  type="button"
                  onClick={() => updateTime(undefined, undefined, period)}
                  className={`block w-full px-4 py-2.5 text-sm font-semibold transition ${
                    selected.period === period
                      ? 'bg-primary text-white'
                      : 'text-black hover:bg-slate-100 dark:text-white dark:hover:bg-meta-4'
                  }`}
                >
                  {period}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {helperText && !error && (
        <p className="mt-1 text-xs text-body dark:text-bodydark2">
          {helperText}
        </p>
      )}

      {error && <p className="mt-2 text-sm text-danger">{error}</p>}
    </div>
  );
};

export default TimePicker;